import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Text,
  ActionList,
  ActionMenu,
  Button,
  ButtonGroup
} from '@primer/react';
import {
  SyncIcon,
  DownloadIcon,
  AlertIcon,
  PeopleIcon
} from '@primer/octicons-react';
import { Dialog } from "@primer/react/drafts";
import { useSelector } from 'react-redux';
import { useCommonContext } from '../../../../contexts/common';
import { danhSachThiApi } from '../../../../api/danhSachThiApi';
import { NotifyHelper } from '../../../../helpers/toast';
import DataTable from '../../../../components/ui/data-table';
import MyButton from '../../../../components/ui/button/Button';
import CapNhatTrangThaiModal from './CapNhatTrangThaiModal';
import { useCommonSelectedHook } from '../../../../hooks/useCommonSelectedHook';
import moment from 'moment';

interface DanhSachThiSinhProps {
  kyThiKetThuc: boolean;
  dm_truong_id: number;
  ex_kythi_id: number;
  ex_phongthi_id: number;
  ex_kythi_monthi_id: number;
}

interface ThiSinhData {
  id: number;
  ten_phong: string;
  trang_thai: string;
  so_bao_danh: string;
  ngaythi_dauvao_dukien: string;
  ma_hs: string;
  ho_ten: string;
  ngay_sinh: string;
  gioi_tinh: string;
  ten_khoi: string;
  ten_he: string;
  isVangThi: boolean;
  ghi_chu: string;
}

const DanhSachThiSinh: React.FC<DanhSachThiSinhProps> = ({
  kyThiKetThuc,
  dm_truong_id,
  ex_kythi_id,
  ex_phongthi_id,
  ex_kythi_monthi_id
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [thiSinhs, setThiSinhs] = useState<ThiSinhData[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showCapNhat, setShowCapNhat] = useState(false);

  const { translate } = useCommonContext();
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  

  const loadThiSinhs = useCallback(async () => {
    if (ex_kythi_monthi_id > 0) {
      setIsLoading(true);
      try {
        const res = await danhSachThiApi.selectThiSinh(
          ex_phongthi_id || 0,
          ex_kythi_monthi_id || 0
        );
        if (res.is_success) {
          setThiSinhs(res.data);
        } else {
          setThiSinhs([]);
        }
      } catch (error) {
        NotifyHelper.Error(translate('Error.LoadDataFailed'));
        setThiSinhs([]);
      }
      setIsLoading(false);
    } else {
      setThiSinhs([]);
    }
  }, [ex_kythi_monthi_id, ex_phongthi_id]);

  useEffect(() => {
    loadThiSinhs();
  }, [loadThiSinhs]);

  const handleExport = () => {
    // Implement export functionality
    console.log('Export to Excel');
  };

  return (
    <Box>     

      <DataTable
      height='calc(100vh - 300px)'
        data={thiSinhs}
        isLoading={isLoading}
        searchEnable
        paging={{ enable: true, pageSizeItems: [10, 20, 50, 200, 500] }}
        selection={!kyThiKetThuc ? {
          mode: 'multiple',
          selectedRowKeys: selectedIds,
          onSelectionChanged: setSelectedIds
        } : undefined}
        actionComponent={
          <Box sx={{ display: 'flex', gap: 2 }}>
            {kyThiKetThuc && (
                          <Box
                            sx={{
                              bg: "danger.emphasis",
                              color: "white",
                              px: 2,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Text sx={{ fontWeight: "bold" }}>
                              {translate("Kỳ thi đã kết thúc")}
                            </Text>
                          </Box>
                        )}
            <MyButton
              text="Cập nhật trạng thái"
              leadingVisual={PeopleIcon}
              disabled={selectedIds.length === 0 || kyThiKetThuc}
              onClick={() => setShowCapNhat(true)}
              variant="primary"
            />
            <MyButton
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              onClick={loadThiSinhs}
            />
            {/* <MyButton
              text="Base.Label.Export"
              leadingVisual={DownloadIcon}
              onClick={handleExport}
            /> */}
          </Box>
        }
        columns={[
          {
            id: 'index',
            caption: translate('TT'),
            width: '50px',
            align: 'center',
            cellRender: (rowData: any) => (
              <Text>{thiSinhs.indexOf(rowData) + 1}</Text>
            )
           },
          {
            dataField: 'ten_phong',
            caption: translate('Phòng thi'),
            width: '100px'
          },
          {
            dataField: 'trang_thai',
            caption: translate('Trạng thái'),
            width: '100px'
          },
          {
            dataField: 'so_bao_danh',
            caption: translate('Số báo danh'),
            width: '100px'
          },
          {
            dataField: 'ngaythi_dauvao_dukien',
            caption: translate('Ngày dự thi dự kiến'),
            width: '120px'
          },
          {
            dataField: 'ma_hs',
            caption: translate('Mã học sinh'),
            width: '120px'
          },
          {
            dataField: 'ho_ten',
            caption: translate('Họ tên'),
            width: '150px'
          },
          {
            dataField: 'ngay_sinh',
            caption: translate('Ngày sinh'), 
            width: '100px',
            cellRender: (data: any) => {
              if (!data.ngay_sinh) return <Text>-</Text>;
              
              const date = moment(data.ngay_sinh);
              if (!date.isValid()) return <Text>-</Text>;
              
              return <Text>{date.format('DD/MM/YYYY')}</Text>;
            }
          },
          {
            dataField: 'gioi_tinh',
            caption: translate('Giới tính'),
            width: '100px'
          },
          {
            dataField: 'ten_khoi',
            caption: translate('Khối'),
            width: '100px'
          },
          {
            dataField: 'ten_he',
            caption: translate('Hệ'),
            width: '100px'
          },
          {
            dataField: 'isVangThi',
            caption: translate('Vắng thi'), 
            width: '100px',
            align: 'center',
            cellRender: (data: ThiSinhData) => (
              <input
                type="checkbox"
                checked={data.isVangThi}
                
                style={{ cursor: 'default' }}
              />
            )
           },
          {
            dataField: 'ghi_chu',
            caption: translate('Ghi chú'),
            width: '100px'
          }
        ]}
      />

      {showCapNhat && (
        <CapNhatTrangThaiModal
          onCancel={() => setShowCapNhat(false)}
          onSuccess={() => {
            setShowCapNhat(false);
            loadThiSinhs();
          }}
          is_show_model={showCapNhat}
          ex_danhsachthi_ids={selectedIds}
        />
      )}
    </Box>
  );
};

export default DanhSachThiSinh;